import type { CmmdThreadAttachment } from "@cmmd-forge/contracts";

import { CmmdApiError } from "../environments/cmmdApi/transport";
import { cmmdApiClient } from "../environments/cmmdApi";
import { getPosthogFeatureFlag } from "../observability/posthog";

/**
 * The cloud persistence lane for composer file attachments (forge#3516).
 *
 * The runtime lane stays authoritative for the turn: bytes still go to the
 * environment first, exactly as before, and a cloud failure never blocks a
 * send. What this lane adds is a second copy in CMMD object storage, uploaded
 * through the forge-context thread-attachment surface, so an attachment
 * outlives the runtime that received it and a later device can still fetch it.
 *
 * Auth is terminal here, by the same rule the shared runtime classifier
 * enforces (401/403 bypass every backoff): the transport has already fired its
 * `onAuthExpired` handler by the time the error surfaces, so this module only
 * needs to NOT loop and NOT swallow the signal — it rethrows auth-classified
 * errors and never retries anything.
 */

export const THREAD_ATTACHMENT_CLOUD_SYNC_FLAG = "forge_thread_attachments_cloud_sync";

let flagReader: (flagKey: string) => boolean = getPosthogFeatureFlag;

/** Test seam. Pass null to restore the default reader. */
export function setThreadAttachmentCloudFlagReader(
  reader: ((flagKey: string) => boolean) | null,
): void {
  flagReader = reader ?? getPosthogFeatureFlag;
}

/** Fails closed: no PostHog, no flag, no cloud sync. */
export function isThreadAttachmentCloudSyncEnabled(): boolean {
  return flagReader(THREAD_ATTACHMENT_CLOUD_SYNC_FLAG);
}

/** 401 and 403 alike: the transport owns recovery, retrying is a storm. */
function isTerminalAuthError(error: unknown): boolean {
  return (
    CmmdApiError.isAuth(error) || (error instanceof CmmdApiError && error.kind === "permission")
  );
}

/**
 * Mirror one uploaded composer file into CMMD. Resolves with the cloud row on
 * success, null when the lane is off, the signal cancelled the copy, or the
 * copy failed for a non-auth reason (all ordinary: the runtime already has the
 * bytes). Rejects only on an auth-classified failure, where swallowing the
 * error would hide the logout the transport just triggered.
 */
export async function syncThreadAttachmentToCloud(input: {
  readonly threadId: string;
  readonly file: File;
  readonly attachmentId?: string | undefined;
  readonly signal?: AbortSignal | undefined;
}): Promise<CmmdThreadAttachment | null> {
  if (!isThreadAttachmentCloudSyncEnabled()) return null;
  input.signal?.throwIfAborted();
  try {
    return await cmmdApiClient.uploadThreadAttachment(
      input.threadId,
      input.file,
      input.attachmentId,
      input.signal,
    );
  } catch (error: unknown) {
    // A cancelled mirror is not a failure: the send that owned it is already
    // gone, so return quietly instead of warning about it.
    if (input.signal?.aborted) return null;
    if (isTerminalAuthError(error)) throw error;
    // A failed mirror is not a failed send. The runtime lane already holds the
    // bytes; note it and move on.
    console.warn("[threadAttachmentCloudLane] cloud upload skipped", {
      attachmentId: input.attachmentId,
      message: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Mirror a whole batch of sent attachments. Best-effort per file and awaited by
 * nobody on the send path; the returned promise never rejects.
 */
export function syncThreadAttachmentsToCloud(input: {
  readonly threadId: string;
  readonly files: ReadonlyArray<{ readonly file: File; readonly attachmentId?: string }>;
  readonly signal?: AbortSignal;
}): Promise<ReadonlyArray<CmmdThreadAttachment | null>> {
  if (!isThreadAttachmentCloudSyncEnabled()) {
    return Promise.resolve(input.files.map(() => null));
  }
  return Promise.all(
    input.files.map((entry) =>
      syncThreadAttachmentToCloud({
        threadId: input.threadId,
        file: entry.file,
        attachmentId: entry.attachmentId,
        signal: input.signal,
      }).catch(() => null),
    ),
  );
}
